/**
 * conceptPick — 개념 고르기의 순수 계산. 개념 목록 창(ConceptListModal, 설계 FE-20260913 §2-1)이 쓴다.
 *
 * 사전(v2 /api/v2/mathcepts) 항목: concept_id · concept_name · order(교육과정 순서의 전역 순번, 유니크) · concept_type(8종)
 * · math_type(AL·AN·GE·PS) · is_outdated · tags(태그 이름 배열). 목록은 **첫 번째 태그**로 묶는다 — 학습 화면 개념 표와
 * AI트래커의 「태그 분류 필터(첫 번째 태그 기준)」와 같은 분류다(오너 2026-09-13). 이것이 기본 보기이고, 순번순(처음 설계의
 * 순번 백 단위 구간)과 가나다순(첫소리 묶음)은 골라 쓰는 보기다(오너 2026-09-13 「가나다순·순번순은 필터 종류의 하나」).
 * matchConcepts · resolveSource · CONCEPT_MATCH_HINT 는 옛 개념 판(ConceptPopover — 2026-09-13 개념 목록 창으로 대체되며
 * 제거)에서 옮겨 왔다. 규칙은 그대로다.
 */
import { expandOrderRangeFor } from './syllabusQuery';

export const MAX_MATCHES = 50;

export const CONCEPT_MATCH_HINT = {
  any: '고른 개념 가운데 하나라도 있는 문제',
  all: '고른 개념을 모두 가진 문제',
  exact: '개념이 고른 것과 정확히 같은 문제',
};

const toInfo = (c) => ({
  id: c.id ?? c.concept_id,
  name: c.name ?? c.concept_name ?? null,
  order: c.order ?? null,
  type: c.concept_type ?? null,
  area: c.math_type ?? null,
  outdated: !!c.is_outdated,
  tag: Array.isArray(c.tags) && c.tags[0] ? String(c.tags[0]) : null,
});

/**
 * 사전에서 후보 찾기 — 순번·번호 완전일치 → 앞글자 일치(이름·순번·번호) → 부분일치 순. 폐기 개념은 단계와 상관없이 맨 뒤
 * (폐기끼리는 다시 같은 단계 순서). 같은 순위끼리는 사전 순서(교육과정 순번)를 지킨다.
 */
export function matchConcepts(dict, query, max = MAX_MATCHES) {
  const q = String(query || '').trim().toLowerCase();
  if (!q || !Array.isArray(dict)) return [];
  const scored = [];
  dict.forEach((c, k) => {
    if (!c) return;
    const info = toInfo(c);
    if (info.id == null) return;
    const name = String(info.name || '').toLowerCase();
    const order = info.order == null ? '' : String(info.order);
    const sid = String(info.id);
    let rank;
    if (order === q || sid === q) rank = 0;
    else if (name.startsWith(q) || order.startsWith(q) || sid.startsWith(q)) rank = 1;
    else if (name.includes(q) || order.includes(q) || sid.includes(q)) rank = 2;
    else return;
    scored.push({ info, rank: rank + (info.outdated ? 10 : 0), k });
  });
  scored.sort((a, b) => a.rank - b.rank || a.k - b.k);
  return scored.slice(0, max).map((s) => s.info);
}

const sameSet = (a, b) => {
  const x = new Set((a || []).map(String));
  const y = new Set((b || []).map(String));
  return x.size === y.size && [...x].every((v) => y.has(v));
};

/**
 * 반영할 순번 범위 유래 — 이번에 채운 범위가 있으면 그것, 없으면 연 항의 유래가 후보다.
 * 후보를 펼친 값과 최종 값이 같은 집합이면 그 유래를, 아니면 null(손으로 고친 값). 연 항을 건드리지 않았으면 옛 유래 유지.
 * 남기면 「순번 120~122에서 펼침」이 사실이 아닌 채 저장된다. 펼치기는 연 항의 방식대로(「제외」면 폐기 개념까지).
 */
export function resolveSource({ picked, dict, filled, term }) {
  const cand = filled || term?.source || null;
  if (!cand) return null;
  const exp = expandOrderRangeFor(dict, cand.order_from, cand.order_to, term?.match);
  if (exp.length && sameSet(exp, picked)) return { kind: 'range', order_from: cand.order_from, order_to: cand.order_to };
  if (!filled && term?.source && sameSet(term.values, picked)) return { ...term.source };
  return null;
}

const byOrder = (a, b) => ((a.order ?? Infinity) - (b.order ?? Infinity)) || (Number(a.id) - Number(b.id));

/** 사전 → 목록 칸. 순번 순이고 폐기 개념은 맨 뒤(설계 §2-1 「폐기 개념은 목록 끝으로」) */
export function conceptEntries(dict) {
  const list = (Array.isArray(dict) ? dict : []).filter((c) => c && (c.id ?? c.concept_id) != null).map(toInfo);
  return [...list.filter((c) => !c.outdated).sort(byOrder), ...list.filter((c) => c.outdated).sort(byOrder)];
}

/** 찾기어 · 영역 · 유형으로 목록을 줄인다 — 고른 것은 거르지 않는다(보이는 칸만 줄인다) */
export function filterEntries(entries, { q = '', area = '', type = '' } = {}) {
  const s = String(q || '').trim().toLowerCase();
  return (entries || []).filter((c) => (!area || c.area === area) && (!type || c.type === type) && (
    !s || String(c.name || '').toLowerCase().includes(s) || String(c.order ?? '').includes(s) || String(c.id).includes(s)
  ));
}

/**
 * 묶는다 — [{ key, label, rank, items }]. 처음 나온 순서대로 쌓고 rank 가 큰 묶음(태그 없음 · 영문·기호 · 폐기)을 뒤로 민다.
 * 정렬이 안정적이라 같은 rank 끼리는 처음 나온 순서 그대로다. 폐기 개념은 어느 보기든 「폐기된 개념」 한 묶음으로 맨 뒤
 */
function bucket(entries, keyOf) {
  const groups = [];
  const at = new Map();
  (entries || []).forEach((c) => {
    const [key, label, rank = 0] = c.outdated ? ['outdated', '폐기된 개념', 99] : keyOf(c);
    if (!at.has(key)) { at.set(key, { key, label, rank, items: [] }); groups.push(at.get(key)); }
    at.get(key).items.push(c);
  });
  return groups.sort((a, b) => a.rank - b.rank);
}

/**
 * 분류별(기본 보기) — 첫 번째 태그로 묶는다. 목록이 순번 순이라 처음 나온 순서 = 그 태그의 가장 앞 순번 순, 곧 매스컨셉 순서다
 * (오너 2026-09-13 「기본은 공통수학 등 분류가 먼저, 매스컨셉 순서대로」). 태그 없는 개념은 「태그 없음」으로 폐기 앞에
 */
export const groupTags = (entries) => bucket(entries, (c) => (c.tag ? [`tag:${c.tag}`, c.tag] : ['none', '태그 없음', 1]));

/** 순번 백 단위 구간 — 2530 → 2500. 순번이 없으면 null */
export const bandOf = (order) => (order != null && Number.isFinite(Number(order)) ? Math.floor(Number(order) / 100) * 100 : null);

/** 순번순 — 순번 백 단위 구간(처음 설계의 묶음). 순번 없는 개념은 폐기 앞 */
export const groupBands = (entries) => bucket(entries, (c) => {
  const b = bandOf(c.order);
  return b == null ? ['none', '순번 없음', 1] : [String(b), `순번 ${b}번대`];
});

// 한글 음절의 첫소리 19자 자리에 맞춘 묶음 이름 — 된소리는 예사소리 묶음에 넣는다(ㄲ → ㄱ, 사전과 같다)
const CHO_BASE = 'ㄱㄱㄴㄷㄷㄹㅁㅂㅂㅅㅅㅇㅈㅈㅊㅋㅌㅍㅎ';
const CHO_ORDER = 'ㄱㄴㄷㄹㅁㅂㅅㅇㅈㅊㅋㅌㅍㅎ';

/** 이름의 글자만 — 수식 표시($ \ { })를 걷는다. 가나다 정렬과 첫소리 판정에 쓴다 */
export const plainName = (name) => String(name || '').replace(/[$\\{}]/g, '').trim();

/** 첫소리 — 「다항식」 → ㄷ, 「까치」 → ㄱ. 한글 음절로 시작하지 않으면 null */
export function initialOf(name) {
  const code = plainName(name).charCodeAt(0);
  if (!(code >= 0xac00 && code <= 0xd7a3)) return null;
  return CHO_BASE[Math.floor((code - 0xac00) / 588)];
}

// 숫자는 크기대로 — 「경우의 수 2」가 「경우의 수 10」 앞, 「2차식」이 「10차」 앞
const byName = (a, b) => plainName(a.name).localeCompare(plainName(b.name), 'ko', { numeric: true }) || byOrder(a, b);

/** 가나다순 — 이름 순으로 세워 첫소리(ㄱ~ㅎ)로 묶는다. 한글로 시작하지 않는 이름(x절편 · Vandermonde …)은 「영문·기호」 */
export const groupInitials = (entries) => bucket([...(entries || [])].sort(byName), (c) => {
  const i = initialOf(c.name);
  return i ? [`ini:${i}`, i, CHO_ORDER.indexOf(i)] : ['etc', '영문·기호', CHO_ORDER.length];
});

/** 목록 보기 셋 — 첫째가 기본. nav 는 왼쪽 목차의 이름 */
export const SORTS = [
  { key: 'tag', label: '분류별', nav: '분류' },
  { key: 'order', label: '순번순', nav: '순번 구간' },
  { key: 'name', label: '가나다순', nav: '가나다' },
];

/** 보기에 맞춰 묶는다 — 모르는 보기는 기본(분류별) */
export function groupConcepts(entries, sort = 'tag') {
  if (sort === 'order') return groupBands(entries);
  if (sort === 'name') return groupInitials(entries);
  return groupTags(entries);
}
